import AdminExport from '@/components/admin/export/admin-export.vue';
import AppointmentDetails from '@/components/appointments/appointments.vue';
import ManageCallList from '@/components/call-management/manage-call-list.vue';
import CallList from '@/components/calls/call-list.vue';
import ListClients from '@/components/client/list-clients/list-clients.vue';
import ViewClient from '@/components/client/view-client/view-client.vue';
import ManageDuplicateClients from '@/components/data-management/duplicate-clients/manage-duplicate-clients.vue';
import DataImport from '@/components/data/data-import/data-imports.vue';
import ExceptionReports from '@/components/exception-reports/exception-reports.vue';
import Dashboard from '@/components/home/dashboard.vue';
import PartnerPractices from '@/components/settings/partner-practices/partner-practices.vue';
import SystemSettings from '@/components/settings/system-settings/system-settings.vue';
import Types from '@/components/settings/types/types.vue';
import Users from '@/components/settings/users/users.vue';
import TargetTrackerDetails from '@/components/target-tracker/target-tracker.vue';
import TaskDetails from '@/components/tasks/task-list/tasks.vue';
import Appointments from '@/views/appointments.vue';
import CallManagement from '@/views/call-management.vue';
import Calls from '@/views/calls.vue';
import Clients from '@/views/clients.vue';
import Data from '@/views/data.vue';
import Forbidden from '@/views/forbidden.vue';
import Home from '@/views/home.vue';
import NotFound from '@/views/not-found.vue';
import Profile from '@/views/profile.vue';
import Settings from '@/views/settings.vue';
import TargetTracker from '@/views/target-tracker.vue';
import Tasks from '@/views/tasks.vue';
import { RouteConfig } from 'vue-router';

const dashboard: RouteConfig = {
    path: '',
    name: 'dashboard',
    component: Dashboard,
    meta: {
        title: 'Dashboard',
        requiresAuthentication: true,
        ignoreBreadcrumb: true,
    },
};

const home: RouteConfig = {
    path: '/',
    name: 'home',
    component: Home,
    meta: {
        title: 'Home',
        requiresAuthentication: true,
    },
    redirect: { name: 'dashboard' },
    children: [
        dashboard,
    ],
};

const notFound: RouteConfig = {
    path: '*',
    name: 'not-found',
    meta: {
        title: 'Not Found',
        requiresAuthentication: false,
    },
    component: NotFound,
};

const forbidden: RouteConfig = {
    path: '/forbidden',
    name: 'forbidden',
    meta: {
        title: 'Forbidden',
        requiresAuthentication: false,
    },
    component: Forbidden,
};

const dataImport: RouteConfig = {
    path: 'import',
    name: 'data-import',
    component: DataImport,
    meta: {
        title: 'Data Import',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
        ],
    },
};

const adminExport: RouteConfig = {
    path: 'export',
    name: 'admin-export',
    component: AdminExport,
    meta: {
        title: 'Data Exports',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'Head of Customer Experience Team',
            'Customer experience executive team leader',
            'Data creation team leader',
        ],
    },
};

const exceptionReports: RouteConfig = {
    path: 'exception-reports',
    name: 'exception-reports',
    component: ExceptionReports,
    meta: {
        title: 'Exception Reports',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'PP Key Stakeholder',
            'Data creation team leader',
            'Customer experience executive team leader',
            'Head of Customer Experience Team',
        ],
    },
};

const manageDuplicateClients: RouteConfig = {
    path: 'manage-duplicate-clients',
    name: 'manage-duplicate-clients',
    component: ManageDuplicateClients,
    meta: {
        title: 'Manage Duplicate Clients',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'Manage duplicates',
        ],
    },
};

const data: RouteConfig = {
    path: '/data',
    name: 'data',
    component: Data,
    meta: {
        title: 'Data',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'PP Key Stakeholder',
            'Head of Customer Experience Team',
            'Customer experience executive team leader',
            'Data creation team leader',
            'Manage duplicates',
        ],
    },
    redirect: { name: 'data-import' },
    children: [
        dataImport,
        adminExport,
        exceptionReports,
        manageDuplicateClients,
    ],
};

const systemSettings: RouteConfig = {
    path: 'system-settings',
    name: 'system-settings',
    component: SystemSettings,
    meta: {
        title: 'System Settings',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
        ],
    },
};

const types: RouteConfig = {
    path: 'types',
    name: 'types',
    component: Types,
    meta: {
        title: 'Types',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
        ],
    },
};

const users: RouteConfig = {
    path: 'users',
    name: 'users',
    component: Users,
    meta: {
        title: 'Users',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
        ],
    },
};

const partnerPractices: RouteConfig = {
    path: 'partner-practices',
    name: 'partner-practices',
    component: PartnerPractices,
    meta: {
        title: 'Partner Practices',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
        ],
    },
};

const settings: RouteConfig = {
    path: '/settings',
    name: 'settings',
    component: Settings,
    meta: {
        title: 'Settings',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
        ],
    },
    redirect: { name: 'system-settings' },
    children: [
        systemSettings,
        types,
        users,
        partnerPractices,
    ],
};

const viewClient: RouteConfig = {
    path: `:id([0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12})`,
    name: 'view-client',
    component: ViewClient,
    meta: {
        title: '',
        requiresAuthentication: true,
    },
    // NOTE(Dan): Removing this prevents the breadcrumb being reactive when changing between 2 clients (where the component remains the same).
    //            Who knows why, but the combination of this, `loadClientAsync` in the `ViewClient` component and the `Breadcrumb` component
    //            seem to be working as required with this combination. ¯\_(ツ)_/¯
    beforeEnter: (to, from, next) => {
        to.meta.breadcrumbTitle = `${to.params.id}`;
        next();
    },
};

const listClients: RouteConfig = {
    path: '',
    name: 'list-clients',
    component: ListClients,
    meta: {
        title: 'Clients',
        requiresAuthentication: true,
        ignoreBreadcrumb: true,
    },
};

const clients: RouteConfig = {
    path: '/clients',
    name: 'clients',
    component: Clients,
    meta: {
        title: 'Clients',
        requiresAuthentication: true,
    },
    redirect: { name: 'list-clients' },
    children: [
        listClients,
        viewClient,
    ],
};

const appointmentDetails: RouteConfig = {
    path: '',
    name: 'appointment-details',
    component: AppointmentDetails,
    meta: {
        title: 'Appointments',
        requiresAuthentication: true,
        ignoreBreadcrumb: true,
    },
};

const appointments: RouteConfig = {
    path: '/appointments',
    name: 'appointments',
    component: Appointments,
    meta: {
        title: 'Appointments',
        requiresAuthentication: true,
    },
    redirect: { name: 'appointment-details' },
    children: [
        appointmentDetails,
    ],
};

const targetTrackerDetails: RouteConfig = {
    path: '',
    name: 'target-tracker-details',
    component: TargetTrackerDetails,
    meta: {
        title: 'Legal Advisor Target Tracker',
        requiresAuthentication: true,
        ignoreBreadcrumb: true,
    },
};

const targetTracker: RouteConfig = {
    path: '/target-tracker',
    name: 'target-tracker',
    component: TargetTracker,
    meta: {
        title: 'Target Tracker',
        requiresAuthentication: true,
    },
    redirect: { name: 'target-tracker-details' },
    children: [
        targetTrackerDetails,
    ],
};

const taskDetails: RouteConfig = {
    path: '',
    name: 'task-details',
    component: TaskDetails,
    meta: {
        title: 'Tasks',
        requiresAuthentication: true,
        ignoreBreadcrumb: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'Customer experience executive',
            'Customer experience executive team leader',
            'Head of Customer Experience Team',
            'Data creation executive',
            'Data creation team leader',
            'Probate legal advisor',
        ],
    },
};

const tasks: RouteConfig = {
    path: '/tasks',
    name: 'tasks',
    component: Tasks,
    meta: {
        title: 'Tasks',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'Customer experience executive',
            'Customer experience executive team leader',
            'Head of Customer Experience Team',
            'Data creation executive',
            'Data creation team leader',
            'Probate legal advisor',
        ],
    },
    redirect: { name: 'task-details' },
    children: [
        taskDetails,
    ],
};

const callList: RouteConfig = {
    path: '',
    name: 'call-list',
    component: CallList,
    meta: {
        title: 'Calls',
        requiresAuthentication: true,
        ignoreBreadcrumb: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'Customer experience executive team leader',
            'Customer experience executive',
            'Head of Customer Experience Team',
        ],
    },
};

const calls: RouteConfig = {
    path: '/calls',
    name: 'calls',
    component: Calls,
    meta: {
        title: 'Calls',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'Customer experience executive team leader',
            'Customer experience executive',
            'Head of Customer Experience Team',
        ],

    },
    redirect: { name: 'call-list' },
    children: [
        callList,
    ],
};

const manageCallList: RouteConfig = {
    path: '',
    name: 'manage-call-list',
    component: ManageCallList,
    meta: {
        title: 'Call Management',
        requiresAuthentication: true,
        ignoreBreadcrumb: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'Customer experience executive team leader',
            'Head of Customer Experience Team',
        ],
    },
};

const callManagement: RouteConfig = {
    path: '/call-management',
    name: 'call-management',
    component: CallManagement,
    meta: {
        title: 'Call Management',
        requiresAuthentication: true,
        requiresAnyRole: [
            'System',
            'Administrator',
            'Internal Key Stakeholder',
            'Customer experience executive team leader',
            'Head of Customer Experience Team',
        ],
    },
    redirect: { name: 'manage-call-list' },
    children: [
        manageCallList,
    ],
};

const profile: RouteConfig = {
    path: '/my-profile',
    name: 'my-profile',
    component: Profile,
    meta: {
        title: 'My Profile',
        requiresAuthentication: true,
    },
};

const routes: Array<RouteConfig> = [
    home,
    clients,
    appointments,
    targetTracker,
    tasks,
    calls,
    callManagement,
    data,
    settings,
    profile,
    notFound,
    forbidden,
];

export default routes;
